import { useTranslation } from 'react-i18next'
import { Modal } from '@/shared/ui/Modal'
import { Spinner } from '@/shared/ui/Spinner'
import { DirhamAmount } from '@/shared/ui/Dirham'
import { cn, localized } from '@/shared/utils'
import { useWalletHistory } from '@/api/hooks'
import { ArrowUpCircle, ArrowDownCircle, ArrowRight, User as UserIcon, Clock } from 'lucide-react'
import type { WalletStudent } from './EditWalletBalanceModal'

function fmtDateTime(s?: string | null) {
  if (!s) return '—'
  const d = new Date(s)
  return isNaN(d.getTime()) ? s : d.toLocaleString('en-US', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

// Audit trail for manual wallet edits: GET v2/admin/wallet/edit-history?user_id=
export function WalletHistoryModal({ open, onClose, student }: {
  open: boolean; onClose: () => void; student: WalletStudent | null
}) {
  const { t, i18n } = useTranslation()
  const historyQ = useWalletHistory(student?.id, open)
  const rows = historyQ.data ?? []
  const name = student ? localized(student, 'name', i18n.language) : ''

  return (
    <Modal open={open} onClose={onClose} title={t('studentWallet.history.title', { name })} size="md">
      {historyQ.isLoading ? (
        <div className="flex justify-center py-10"><Spinner /></div>
      ) : historyQ.isError ? (
        <p className="text-sm text-red-500 text-center py-10">{t('studentWallet.history.loadError')}</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-10">{t('studentWallet.history.empty')}</p>
      ) : (
        <div className="space-y-2.5 max-h-[60vh] overflow-y-auto pe-1">
          {rows.map(row => (
            <div key={row.id} className="rounded-xl border border-gray-100 p-3">
              <div className="flex items-center justify-between gap-2">
                <span className={cn(
                  'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold',
                  row.action === 'credit' ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'
                )}>
                  {row.action === 'credit' ? <ArrowUpCircle size={12} /> : <ArrowDownCircle size={12} />}
                  {row.action === 'credit' ? t('studentWallet.modal.credit') : t('studentWallet.modal.debit')}
                </span>
                <DirhamAmount value={row.amount} size={13} />
              </div>

              <div className="mt-2 flex items-center gap-1.5 text-xs text-gray-500">
                <span>AED {Number(row.previous_balance).toLocaleString('en-AE')}</span>
                <ArrowRight size={11} className="text-gray-300 flex-shrink-0" />
                <span className="font-semibold text-gray-800">AED {Number(row.new_balance).toLocaleString('en-AE')}</span>
              </div>

              {row.reason && <p className="mt-2 text-xs text-gray-600 break-words">{row.reason}</p>}

              <div className="mt-2 flex items-center justify-between gap-2 text-[11px] text-gray-400">
                <span className="flex items-center gap-1 min-w-0 truncate"><UserIcon size={10} className="flex-shrink-0" />{row.admin_name || `#${row.admin_id}`}</span>
                <span className="flex items-center gap-1 flex-shrink-0"><Clock size={10} />{fmtDateTime(row.created_at)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  )
}
